import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  FileUpload,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { ModelError } from 'src/front/api/generated';

import previewIcon from 'src/front/assets/icon_preview.svg';
import { ACCEPTED_FILE_UPLOAD_FORMATS } from 'src/front/common/constants/files';
import { isInvalidFileError } from 'src/front/common/utils/errorUtils';
import { checkAcceptedFileType, openDocument } from 'src/front/common/utils/fileUploadUtils';

import { formatFreeText } from 'src/front/common/utils/formUtils';
import { DocumentUploadSchema } from 'src/front/common/utils/validationSchemas';

import FormModal from 'src/front/components/FormModal';
import { FormElement, ResetButton, Subtitle, Title } from 'src/front/components/FormStyles';
import styled from 'styled-components';

export const FormRow = styled(FormElement)`
  margin: 10px 0;
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const StyledResetButton = styled(ResetButton)`
  margin-right: 20px;
`;
export const StyledPreviewButton = styled.button`
  position: relative;

  border: none;
  margin-right: 20px;

  background: none;

  &&:focus {
    outline: none;
  }
`;

export interface UploadDocumentFormData {
  documentType: Option | null;
  files?: FileList | null;
  filePath?: string;
  explanation: string;
  isOldDocument: boolean;
  createDate: string;
  documentId: string;
  documentName: string;
}

const defaultValues = {
  documentType: null,
  files: undefined,
  filePath: '',
  explanation: '',
  isOldDocument: false,
  createDate: '',
  documentId: '',
  documentName: '',
};

interface Props {
  onClose: () => void;
  submit: (data: UploadDocumentFormData) => void;
  formValues?: UploadDocumentFormData;
  documentTypes: Option[];
  error?: ModelError;
  defaultExplanation?: string;
}

const UploadDocumentModal: FC<Props> = ({
  formValues,
  onClose,
  submit,
  documentTypes,
  defaultExplanation,
  error,
}) => {
  const { t } = useTranslation();
  const [previewAvailable, setPreviewAvailable] = useState<boolean>(false);

  const {
    watch,
    register,
    errors,
    setValue,
    getValues,
    handleSubmit,
    control,
    reset,
    formState,
    setError,
  } = useForm<UploadDocumentFormData>({
    defaultValues: {
      ...defaultValues,
      ...formValues,
    },
    resolver: yupResolver(DocumentUploadSchema),
  });

  const registerFields = useCallback(() => {
    register({ name: 'files' });
    register({ name: 'filePath' });
    register({ name: 'isOldDocument' });
    register({ name: 'documentId' });
  }, [register]);

  useEffect(() => {
    if (error && isInvalidFileError(error)) {
      setError('files', { type: 'required', message: 'general_uploadFileFormat_errorMessage' });
    }
  }, [error, setError]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const handleFileSelect = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('files', target.files, { shouldValidate: true, shouldDirty: true });
    setValue('filePath', target.value);
    setPreviewConfiguration(target.files);
  };
  const onReset = (): void => {
    reset();
    registerFields();
  };

  const onPreview = (): void => {
    const { files } = getValues();
    openDocument(files);
  };

  const setPreviewConfiguration = async (data?: FileList | null): Promise<void> => {
    const acceptStatus = await checkAcceptedFileType(data);
    setPreviewAvailable(acceptStatus);
    acceptStatus && onPreview();
  };

  const onSubmit = async (data: UploadDocumentFormData): Promise<void> => {
    await submit({ ...formValues, ...data });
  };

  const isEditMode = Boolean(formValues?.documentId);

  return (
    <FormModal onClose={onClose} zIndex={800}>
      <Title>
        <Trans i18nKey="cardEntry_documentEntry_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_documentEntry_table1_subtitle" />
      </Subtitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <Controller
            control={control}
            name="documentType"
            render={({ onChange, value }) => (
              <Combobox
                dropdownHeight={230}
                errorMessage={t(errors?.documentType?.message as string)}
                items={documentTypes}
                label={t('general_documentType_label')}
                name="documentType"
                onChange={onChange}
                searchable={documentTypes.length > 4}
                value={value}
                required
              />
            )}
          />
        </FormRow>
        <FormRow>
          <FileUpload
            accept={ACCEPTED_FILE_UPLOAD_FORMATS}
            description={t('general_uploadFile_text')}
            disabled={isEditMode}
            errorMessage={t(errors?.files?.message as string)}
            label={t('general_uploadImage_text')}
            name="file"
            onChange={handleFileSelect}
            value={watch('filePath')}
            required
          >
            {previewAvailable ? (
              <StyledPreviewButton onClick={onPreview} type="button">
                <img alt="previewIcon" src={previewIcon} />
              </StyledPreviewButton>
            ) : null}
          </FileUpload>
        </FormRow>
        <FormRow>
          <Controller
            control={control}
            name="explanation"
            render={({ onChange, value }) => (
              <TextInput
                disabled={Boolean(defaultExplanation)}
                errorMessage={t(errors?.explanation?.message as string)}
                formatter={formatFreeText}
                label={t('general_note_label')}
                maxLength={50}
                name="explanation"
                onChange={onChange}
                value={defaultExplanation || value}
              />
            )}
          />
        </FormRow>
        <ButtonRow>
          {formState.isDirty ? (
            <StyledResetButton
              color={ButtonColor.Secondary}
              onClick={onReset}
              size={ButtonSize.Small}
              text={t('general_components_reset_button')}
              type="reset"
            />
          ) : null}
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_save_button')}
            type="submit"
          />
        </ButtonRow>
      </form>
    </FormModal>
  );
};

export default UploadDocumentModal;
